import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddArterialBloodGas } from 'src/app/interfaces/ArterialBloodGas/IAddArterialBloodGas';
import { ArterialBloodGasService } from 'src/app/services/arterialBloodGas.service';
import { dateRangeValidator } from 'src/date/date.configuration';

@Component({
  selector: 'app-arterial-blood-gas-add-edit',
  standalone: true,
  templateUrl: './arterial-blood-gas-add-edit.component.html',
  styleUrls: ['./arterial-blood-gas-add-edit.component.css'],
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
  ],
})
export class ArterialBloodGasAddEditComponent {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isLoading = false;

  isEdit = false;
  editArterialBloodGasId?: number;

  arterialBloodGasSubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  arterialBloodGasForm: FormGroup = new FormGroup({});

  constructor(
    private readonly arterialBloodGasService: ArterialBloodGasService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isEdit = false;
    this.isLoading = false;

    this.arterialBloodGasForm = this.formBuilder.group({
      dateValue: [
        null,
        [Validators.required, dateRangeValidator(undefined, new Date())],
      ],
      phValue: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(10),
        ]),
      ],
      hco3Value: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(100),
        ]),
      ],
      paco2Value: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(150),
        ]),
      ],
      pao2Value: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(250),
        ]),
      ],
      sao2Value: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(150),
        ]),
      ],
    });

    this.arterialBloodGasService.arterialBloodGasEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.editArterialBloodGasId = data?.id;

          this.arterialBloodGasForm.patchValue({
            dateValue:
              data?.onSetDateTime && this.convertToLocale(data?.onSetDateTime),
            phValue: data?.ph,
            hco3Value: data?.hco3,
            paco2Value: data?.paco2,
            pao2Value: data?.pao2,
            sao2Value: data?.sao2,
          });
        } else {
          this.isEdit = false;
          this.editArterialBloodGasId = undefined;

          this?.arterialBloodGasForm.reset();

          this.arterialBloodGasForm.patchValue({
            dateValue: this.convertToLocale(),
          });
        }
      });

    this.arterialBloodGasService.arterialBloodGasModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  handleCancel() {
    this.arterialBloodGasForm.reset();

    this.arterialBloodGasService.arterialBloodGasModalSub.next(false);

    this.arterialBloodGasSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.arterialBloodGasSubmitted = true;

    if (this.arterialBloodGasForm.valid) {
      const arterialBloodGasObj: IAddArterialBloodGas = {};

      arterialBloodGasObj.id = this.editArterialBloodGasId;

      arterialBloodGasObj.onSetDateTime = moment(
        this.arterialBloodGasForm.value?.dateValue
      ).format();

      arterialBloodGasObj.ph = this.arterialBloodGasForm.get('phValue')?.value;
      arterialBloodGasObj.hco3 =
        this.arterialBloodGasForm.get('hco3Value')?.value;
      arterialBloodGasObj.paco2 =
        this.arterialBloodGasForm.get('paco2Value')?.value;
      arterialBloodGasObj.pao2 =
        this.arterialBloodGasForm.get('pao2Value')?.value;
      arterialBloodGasObj.sao2 =
        this.arterialBloodGasForm.get('sao2Value')?.value;

      if (this.isEdit) {
        this.arterialBloodGasService
          .editArterialBloodGas(arterialBloodGasObj)
          .subscribe({
            next: () => {
              this.swalToastService.toastSuccess(
                'Arterial Blood Gas edited successfully'
              );
              this.arterialBloodGasService.getData();

              this.handleCancel();
            },
            error: (err) => {
              this.swalToastService.toastError(
                'Unable to edit Arterial Blood Gas'
              );

              this.handleCancel();
            },
          });
      } else {
        this.arterialBloodGasService
          .addArterialBloodGas(arterialBloodGasObj)
          .subscribe({
            next: () => {
              this.swalToastService.toastSuccess(
                'Arterial Blood Gas added successfully'
              );
              this.arterialBloodGasService.getData();

              this.handleCancel();
            },
            error: (err) => {
              this.swalToastService.toastError(
                'Unable to add Arterial Blood Gas'
              );
            },
          });
      }
    } else {
      this.isLoading = false;
    }
  }

  getCurrentDateTime() {
    return new Date().toISOString().slice(0, 16);
  }

  // CONVERT UTC TO LOCAL
  convertToLocale(date?: string) {
    const currentDateTime = date ? moment(date).format() : moment().format();

    const isoDate = currentDateTime.slice(0, 16);

    return isoDate;
  }
}
